import { useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CircleAlert, UserX } from "lucide-react";
import RouteErrorBoundary from "../../components/shared/RouteErrorBoundary";
import { SectionLoader } from "../../components/shared/Loading";
import { createEmployeeRoutes } from "../../routes/employeeRoutes";
import { ROUTES } from "../../components/shared/constant";
import { isEmployeeUserViewPath, toUserViewPath } from "../../routes/employeePaths";
// Shared UI kit, imported read-only (foundation decision 4).
import { WlmEmptyState } from "@/views/workforce/ui";
import { useEmployeeAccess } from "./access/useEmployeeAccess";

/**
 * Route tree for the skyEmployee module. Rendered by AppLayout when the
 * pathname is under /employee and useEmployeeGate passes.
 *
 * Unlike WorkforceRoutes there is no pack provider or first-run gate:
 * skyEmployee has no per-tenant setup step, so the routes render directly.
 * What the caller may see comes from GET /employee/me: while it loads nothing
 * renders; a caller with no skyEmployee identity is told why, not redirected.
 */
export function EmployeeRoutes() {
  const routes = useMemo(() => createEmployeeRoutes(), []);
  const location = useLocation();
  const access = useEmployeeAccess();

  if (access.isLoading) return <SectionLoader label="Loading Employee Experience…" />;

  if (access.isNotEmployee) {
    return (
      <WlmEmptyState
        icon={UserX}
        message="Your profile isn't set up in Employee Experience yet"
        hint="Ask your administrator to add you."
      />
    );
  }

  if (access.isError) {
    return (
      <WlmEmptyState
        icon={CircleAlert}
        message="Employee Experience couldn't load"
        hint="Refresh the page to try again."
      />
    );
  }

  // Someone with neither the admin nor the manage menu (a plain user) has only
  // the employee view. A page that's in both views (a post, the directory…)
  // opens there as the same page; admin-only pages (and the logo's /employee)
  // open their Home.
  const hasAdminView = access.can("nav.admin") || access.can("nav.manage");
  if (!hasAdminView && !isEmployeeUserViewPath(location.pathname)) {
    return (
      <Navigate
        to={(toUserViewPath(location.pathname) ?? ROUTES.EMPLOYEE_MY_HOME) + location.search}
        replace
      />
    );
  }

  // The shared content wrapper is only min-h-[85vh]; on a short page the
  // white body showed below it. Fill the viewport under the header (56px on
  // phones, 52px from md up, matching AppLayout's top padding).
  return (
    <div className="min-h-[calc(100vh-56px-var(--new-version-h))] md:min-h-[calc(100vh-52px-var(--new-version-h))]">
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.name}
            path={route.path}
            element={
              <RouteErrorBoundary fallbackTitle={`Error loading ${route.name}`}>
                <route.component />
              </RouteErrorBoundary>
            }
          />
        ))}
        {/* /employee and any unknown /employee/* path land on Home — the
          employee-facing Home when the path is in that view. */}
        <Route
          path="*"
          element={
            <Navigate
              to={
                (isEmployeeUserViewPath(location.pathname)
                  ? ROUTES.EMPLOYEE_MY_HOME
                  : ROUTES.EMPLOYEE_HOME) + location.search
              }
              replace
            />
          }
        />
      </Routes>
    </div>
  );
}
